import { useCallback, useEffect, useMemo, useState } from "react";
import ReactFlow, {
  Background,
  type Edge,
  type Node,
  Handle,
  Position,
  ReactFlowProvider,
} from "reactflow";
import "reactflow/dist/style.css";
import { Check, Network, Search, Plus, Sparkles, X } from "lucide-react";
import {
  api,
  type Person,
  type PeopleListResponse,
  type RelationshipsResponse,
  type RelationshipType,
} from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { ViewFilter, useViewFilter } from "../../components/ViewFilter";
import { useRelationshipTaxonomy } from "../../lib/relationship-taxonomy";

const RELATIONSHIP_LABELS: Record<RelationshipType, string> = {
  colleague: "Colleagues",
  family: "Family",
  friend: "Friends",
  vendor: "Vendors",
  client: "Clients",
  manager: "Managers",
  report: "Reports",
  other: "Other",
};

const RELATIONSHIP_COLORS: Record<RelationshipType, string> = {
  colleague: "#6366f1",
  family: "#ec4899",
  friend: "#22c55e",
  vendor: "#f59e0b",
  client: "#14b8a6",
  manager: "#a855f7",
  report: "#8b5cf6",
  other: "#64748b",
};

export function Relationships({
  onOpenPerson,
}: {
  /** Wired by the Explore page to swap to the People anchor and
   *  open the person's detail panel. Optional so the anchor still
   *  works when rendered standalone (e.g. on a future dedicated
   *  /explore/relationships route). */
  onOpenPerson?: (personId: string) => void;
}) {
  const [data, setData] = useState<RelationshipsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const auth = useAuth();
  const { state: filter, setState: setFilter, loaded: filterLoaded } = useViewFilter("relationships");

  const refresh = useCallback(() => {
    api
      .relationshipsList({ include_test: filter.includeTest })
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((e: Error) => setError(e.message));
  }, [filter.includeTest]);

  useEffect(() => {
    if (!filterLoaded) return;
    refresh();
  }, [refresh, filterLoaded]);

  const isEmpty = data && Object.keys(data.byType).length === 0;
  const totalCount = data
    ? Object.values(data.counts).reduce((a, b) => a + b, 0)
    : 0;
  const inferredCount = data?.inferredCount ?? 0;
  const [inferring, setInferring] = useState(false);

  const triggerInfer = useCallback(async () => {
    if (inferring) return;
    setInferring(true);
    try {
      await api.relationshipsInfer();
      refresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
    } finally {
      setInferring(false);
    }
  }, [inferring, refresh]);

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
          Relationships
          {inferredCount > 0 && (
            <span
              className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 align-middle"
              title="Memso has inferred these relationships from your interaction history; tap a row to confirm or reject."
            >
              <Sparkles className="w-3 h-3" />
              {inferredCount} to review
            </span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          <ViewFilter state={filter} onChange={setFilter} />
          <button
            type="button"
            onClick={triggerInfer}
            disabled={inferring}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors disabled:opacity-50"
            title="Scan interactions and emit dashed (inferred) relationships for review"
          >
            <Sparkles className="w-4 h-4" />
            <span className="hidden sm:inline">{inferring ? "Detecting..." : "Auto-detect"}</span>
          </button>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold bg-violet-600 text-white shadow-sm hover:bg-violet-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Tag a relationship</span>
          </button>
        </div>
      </div>

      {error && <div className="text-sm text-rose-600 dark:text-rose-300">{error}</div>}

      {data === null ? (
        <div className="h-72 rounded-2xl bg-slate-100 dark:bg-slate-800/40 animate-pulse" />
      ) : isEmpty ? (
        <EmptyState onAdd={() => setShowAdd(true)} />
      ) : (
        <>
          <RelGraph
            data={data}
            userLabel={auth.user?.name || auth.user?.email || "You"}
            onOpenPerson={onOpenPerson}
          />
          <p className="text-xs text-slate-500 dark:text-slate-400 px-1">
            {totalCount} relationship{totalCount === 1 ? "" : "s"} across{" "}
            {Object.keys(data.byType).length} group
            {Object.keys(data.byType).length === 1 ? "" : "s"}.
          </p>
          <RelGroups data={data} onOpenPerson={onOpenPerson} onChanged={refresh} />
        </>
      )}

      {showAdd && (
        <AddRelationshipDialog
          onClose={() => setShowAdd(false)}
          onCreated={() => {
            setShowAdd(false);
            refresh();
          }}
        />
      )}
    </section>
  );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <section className="glass rounded-2xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-8 sm:p-12 text-center">
      <div className="inline-flex w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-400 to-violet-500 items-center justify-center text-white shadow-md mb-4">
        <Network className="w-7 h-7" strokeWidth={2.5} />
      </div>
      <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Relationships</h2>
      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-md mx-auto">
        Tag the people who matter so Memso can synthesize across them.
      </p>
      <button
        type="button"
        onClick={onAdd}
        className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold bg-violet-600 text-white shadow-sm hover:bg-violet-700"
      >
        <Plus className="w-4 h-4" />
        Tag your first relationship
      </button>
    </section>
  );
}

function RelGraph({
  data,
  userLabel,
  onOpenPerson,
}: {
  data: RelationshipsResponse;
  userLabel: string;
  onOpenPerson?: (personId: string) => void;
}) {
  const { nodes, edges } = useMemo(() => buildGraph(data, userLabel), [data, userLabel]);

  return (
    <div className="h-72 sm:h-96 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden bg-white/40 dark:bg-slate-900/30">
      <ReactFlowProvider>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          nodesDraggable={false}
          nodesConnectable={false}
          edgesFocusable={false}
          panOnDrag
          zoomOnScroll
          proOptions={{ hideAttribution: true }}
          nodeTypes={NODE_TYPES}
          onNodeClick={(_evt, node) => {
            if (node.data?.personId && onOpenPerson) {
              onOpenPerson(node.data.personId as string);
            }
          }}
        >
          <Background gap={22} size={1.4} color="rgba(148,163,184,0.25)" />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
}

function CenterNode({ data }: { data: { label: string } }) {
  return (
    <>
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      <div className="px-4 py-2.5 rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-500 text-white text-sm font-bold shadow-lg border-2 border-white dark:border-slate-900 whitespace-nowrap">
        {data.label}
      </div>
    </>
  );
}

function PersonNode({
  data,
}: {
  data: { label: string; type: RelationshipType; subtitle?: string | null };
}) {
  const color = RELATIONSHIP_COLORS[data.type];
  return (
    <>
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      <div
        className="px-3 py-2 rounded-xl bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-xs font-semibold shadow-md border-2 whitespace-nowrap cursor-pointer hover:shadow-lg transition-shadow"
        style={{ borderColor: color }}
      >
        <div className="flex items-center gap-1.5">
          <span
            className="w-2 h-2 rounded-full inline-block"
            style={{ background: color }}
            aria-hidden="true"
          />
          <span>{data.label}</span>
        </div>
        {data.subtitle && (
          <div className="text-[10px] font-normal text-slate-500 dark:text-slate-400 truncate max-w-[140px]">
            {data.subtitle}
          </div>
        )}
      </div>
    </>
  );
}

const NODE_TYPES = { center: CenterNode, person: PersonNode };

function buildGraph(
  data: RelationshipsResponse,
  userLabel: string,
): { nodes: Node[]; edges: Edge[] } {
  const types = Object.keys(data.byType) as RelationshipType[];
  const nodes: Node[] = [
    {
      id: "user",
      type: "center",
      position: { x: 0, y: 0 },
      data: { label: userLabel },
      draggable: false,
      selectable: false,
    },
  ];
  const edges: Edge[] = [];
  // Cap visible-per-bucket to keep the canvas legible; the grouped
  // list below shows everything.
  const PER_BUCKET = 6;
  const radiusBase = 240;
  const totalTypes = Math.max(types.length, 1);
  types.forEach((type, typeIdx) => {
    const color = RELATIONSHIP_COLORS[type];
    const bucket = data.byType[type].slice(0, PER_BUCKET);
    const baseAngle = (typeIdx / totalTypes) * Math.PI * 2 - Math.PI / 2;
    const spread = Math.PI / Math.max(totalTypes * 1.4, 2.5);
    bucket.forEach((rel, i) => {
      const a =
        bucket.length === 1
          ? baseAngle
          : baseAngle - spread / 2 + (spread * i) / (bucket.length - 1);
      const radius = radiusBase + (i % 2 === 0 ? 0 : 60);
      const x = Math.cos(a) * radius;
      const y = Math.sin(a) * radius;
      const id = `node_${rel.id}`;
      nodes.push({
        id,
        type: "person",
        position: { x, y },
        data: {
          label: rel.person.display_name,
          type,
          subtitle: rel.person.organization,
          personId: rel.person.id,
        },
        draggable: false,
      });
      const isInferred = !rel.confirmed;
      edges.push({
        id: `edge_${rel.id}`,
        source: "user",
        target: id,
        style: {
          stroke: color,
          strokeWidth: isInferred ? 1.5 : 2,
          opacity: isInferred ? 0.55 : 0.85,
          ...(isInferred ? { strokeDasharray: "6 4" } : {}),
        },
        animated: false,
      });
    });
    const overflow = data.byType[type].length - bucket.length;
    if (overflow > 0) {
      const id = `overflow_${type}`;
      const a = baseAngle;
      const x = Math.cos(a) * (radiusBase + 130);
      const y = Math.sin(a) * (radiusBase + 130);
      nodes.push({
        id,
        type: "person",
        position: { x, y },
        data: {
          label: `+${overflow} more`,
          type,
          subtitle: RELATIONSHIP_LABELS[type],
        },
        draggable: false,
      });
      edges.push({
        id: `edge_overflow_${type}`,
        source: "user",
        target: id,
        style: { stroke: color, strokeWidth: 1.5, opacity: 0.4, strokeDasharray: "5 5" },
      });
    }
  });
  return { nodes, edges };
}

function RelGroups({
  data,
  onOpenPerson,
  onChanged,
}: {
  data: RelationshipsResponse;
  onOpenPerson?: (personId: string) => void;
  onChanged: () => void;
}) {
  const taxo = useRelationshipTaxonomy();
  const types = Object.keys(data.byType);

  // Roll every leaf bucket up under its taxonomy root so the user can
  // scan "all 7 of my Work peers" in one section. Off-taxonomy /
  // legacy types fall into an "Other" group keyed by the type itself.
  const roots = new Map<string, { label: string; types: string[]; count: number }>();
  for (const type of types) {
    const rootEntry = taxo.rootOf(type);
    const rootKey = rootEntry?.type ?? type;
    const rootLabel = rootEntry?.display_name ?? taxo.humanize(type);
    const g = roots.get(rootKey) ?? { label: rootLabel, types: [], count: 0 };
    g.types.push(type);
    g.count += data.byType[type].length;
    roots.set(rootKey, g);
  }
  const orderedRoots = [...roots.entries()].sort((a, b) => {
    const sa = taxo.byType.get(a[0])?.sort_order ?? 9999;
    const sb = taxo.byType.get(b[0])?.sort_order ?? 9999;
    return sa - sb;
  });

  return (
    <div className="space-y-4">
      {orderedRoots.map(([rootKey, group]) => (
        <div
          key={rootKey}
          className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/30 overflow-hidden"
        >
          <header className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/40">
            <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
              {group.label}
            </h3>
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
              {group.count}
            </span>
          </header>
          <div className="divide-y divide-slate-100 dark:divide-slate-700/50">
            {group.types
              .slice()
              .sort(
                (a, b) =>
                  (taxo.byType.get(a)?.sort_order ?? 9999) -
                  (taxo.byType.get(b)?.sort_order ?? 9999),
              )
              .map((type) => (
                <div key={type}>
                  <div className="flex items-center gap-2 px-4 py-1.5 bg-white/40 dark:bg-slate-900/20">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{
                        background:
                          RELATIONSHIP_COLORS[type as RelationshipType] ?? "#94a3b8",
                      }}
                      aria-hidden="true"
                    />
                    <h4 className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                      {RELATIONSHIP_LABELS[type as RelationshipType] ??
                        taxo.humanize(type)}
                    </h4>
                    <span className="text-[11px] text-slate-400">
                      {data.byType[type].length}
                    </span>
                  </div>
                  <ul className="divide-y divide-slate-100 dark:divide-slate-700/50">
                    {data.byType[type].map((rel) => (
                      <RelRow
                        key={rel.id}
                        rel={rel}
                        type={type as RelationshipType}
                        onOpenPerson={onOpenPerson}
                        onChanged={onChanged}
                      />
                    ))}
                  </ul>
                </div>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Individual row in the grouped list. Inferred rows render with a
 *  dashed border + "inferred" pill and surface Confirm / Reject
 *  buttons. Confirmed rows render solid with the existing Remove
 *  affordance. Tapping anywhere else opens the person detail panel.
 *
 *  Acting on an inferred row optimistically updates the row state so
 *  the user gets immediate feedback before the API round-trip; the
 *  parent then refreshes from the server to lock the canonical state. */
function RelRow({
  rel,
  type,
  onOpenPerson,
  onChanged,
}: {
  rel: RelationshipsResponse["byType"][string][number];
  type: RelationshipType;
  onOpenPerson?: (personId: string) => void;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<"confirm" | "reject" | "remove" | null>(null);
  const isInferred = !rel.confirmed && !rel.rejected_at;

  const onConfirm = useCallback(async () => {
    if (busy) return;
    setBusy("confirm");
    try {
      await api.relationshipConfirm(rel.id);
      onChanged();
    } finally {
      setBusy(null);
    }
  }, [busy, rel.id, onChanged]);

  const onReject = useCallback(async () => {
    if (busy) return;
    setBusy("reject");
    try {
      await api.relationshipReject(rel.id);
      onChanged();
    } finally {
      setBusy(null);
    }
  }, [busy, rel.id, onChanged]);

  const onRemove = useCallback(async () => {
    if (busy) return;
    if (!confirm(`Remove this ${type} tag?`)) return;
    setBusy("remove");
    try {
      await api.relationshipDelete(rel.id);
      onChanged();
    } finally {
      setBusy(null);
    }
  }, [busy, rel.id, type, onChanged]);

  return (
    <li
      className={
        isInferred
          ? "bg-amber-50/60 dark:bg-amber-900/10 border-l-2 border-l-amber-400 dark:border-l-amber-500"
          : ""
      }
    >
      <div className="flex items-start gap-3 px-4 py-2.5">
        <button
          type="button"
          onClick={() => onOpenPerson?.(rel.person.id)}
          className="flex flex-col flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-slate-900 dark:text-slate-100 truncate">
              {rel.person.display_name}
            </span>
            {isInferred && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-amber-200 dark:bg-amber-700/40 text-amber-800 dark:text-amber-200">
                <Sparkles className="w-2.5 h-2.5" />
                Inferred
                {typeof rel.inference_confidence === "number" && (
                  <span className="opacity-70">
                    {" "}
                    {Math.round(rel.inference_confidence * 100)}%
                  </span>
                )}
              </span>
            )}
            {(rel.person.role || rel.person.organization) && (
              <span className="text-xs text-slate-500 dark:text-slate-400 truncate hidden sm:inline">
                · {[rel.person.role, rel.person.organization].filter(Boolean).join(" · ")}
              </span>
            )}
          </div>
          {isInferred && rel.inference_reason && (
            <span className="text-[11px] text-amber-700 dark:text-amber-300/80 mt-0.5 truncate">
              {rel.inference_reason}
            </span>
          )}
        </button>
        {isInferred ? (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={onConfirm}
              disabled={!!busy}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-2 py-1 disabled:opacity-50"
              aria-label="Confirm"
            >
              <Check className="w-3 h-3" />
              Confirm
            </button>
            <button
              type="button"
              onClick={onReject}
              disabled={!!busy}
              className="inline-flex items-center text-xs text-slate-500 hover:text-rose-600 dark:hover:text-rose-300 px-2 py-1 rounded-md hover:bg-rose-50 dark:hover:bg-rose-950/30 disabled:opacity-50"
              aria-label="Reject"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={onRemove}
            disabled={!!busy}
            className="text-xs text-slate-400 hover:text-rose-500 dark:hover:text-rose-300 px-2 py-1 rounded-md hover:bg-rose-50 dark:hover:bg-rose-950/30 flex-shrink-0 disabled:opacity-50"
            aria-label={`Remove ${rel.person.display_name} from ${type}`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </li>
  );
}

function AddRelationshipDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<Person[] | null>(null);
  const [picked, setPicked] = useState<Person | null>(null);
  const [type, setType] = useState<RelationshipType>("colleague");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const handle = window.setTimeout(() => {
      api
        .peopleList({ sort: "alpha", search: search || undefined, limit: 30 })
        .then((d: PeopleListResponse) => {
          if (!cancelled) setResults(d.people);
        })
        .catch(() => {
          if (!cancelled) setResults([]);
        });
    }, 120);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [search]);

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-950/40 dark:bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-2xl overflow-hidden flex flex-col max-h-[80vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100">
            Tag a relationship
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <input
              autoFocus
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPicked(null);
              }}
              placeholder="Search people"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {!picked && (
            <ul>
              {results === null ? (
                <li className="px-5 py-6 text-sm text-slate-400 text-center">Searching…</li>
              ) : results.length === 0 ? (
                <li className="px-5 py-6 text-sm text-slate-400 text-center">
                  No matches. Add the person from the People anchor first.
                </li>
              ) : (
                results.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => setPicked(p)}
                      className="w-full text-left px-5 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800/50 border-b border-slate-100 dark:border-slate-700/50"
                    >
                      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {p.display_name}
                      </div>
                      {(p.organization || p.primary_email) && (
                        <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                          {p.organization || p.primary_email}
                        </div>
                      )}
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
          {picked && (
            <form
              className="px-5 py-4 space-y-3"
              onSubmit={async (e) => {
                e.preventDefault();
                setSaving(true);
                setError(null);
                try {
                  await api.relationshipCreate({
                    person_id: picked.id,
                    type,
                    notes: notes.trim() || undefined,
                  });
                  onCreated();
                } catch (err) {
                  setError(err instanceof Error ? err.message : String(err));
                } finally {
                  setSaving(false);
                }
              }}
            >
              <div className="rounded-lg px-3 py-2 bg-violet-50 dark:bg-violet-950/30 border border-violet-200/60 dark:border-violet-800/40 text-sm text-slate-800 dark:text-slate-100 flex items-center justify-between">
                <span className="font-semibold">{picked.display_name}</span>
                <button
                  type="button"
                  onClick={() => setPicked(null)}
                  className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                >
                  Change
                </button>
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {(Object.keys(RELATIONSHIP_LABELS) as RelationshipType[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setType(t)}
                    aria-pressed={type === t}
                    className={`text-xs font-semibold py-2 rounded-lg border transition-colors ${
                      type === t
                        ? "text-white shadow-sm"
                        : "bg-white/60 dark:bg-slate-800/40 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:border-violet-300"
                    }`}
                    style={
                      type === t
                        ? { background: RELATIONSHIP_COLORS[t], borderColor: RELATIONSHIP_COLORS[t] }
                        : undefined
                    }
                  >
                    {RELATIONSHIP_LABELS[t].replace(/s$/, "")}
                  </button>
                ))}
              </div>
              <textarea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notes (optional)"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-400 resize-none"
              />
              {error && <div className="text-xs text-rose-600 dark:text-rose-300">{error}</div>}
              <button
                type="submit"
                disabled={saving}
                className="w-full px-3 py-2 rounded-xl text-sm font-semibold bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
              >
                {saving ? "Tagging…" : "Tag relationship"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
